/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
import { takeLatest, put, select, call } from 'redux-saga/effects';
import moment from 'moment';
import { flattenDeep, uniq, isNil } from 'lodash';
import uuidv4 from 'uuid/v4';
import { setNotification, operatorTyping } from 'utils';
import { getMachinesMacro } from './macros';
import * as RollsOutTypes from '../types/rollOutTypes';
import * as RollsOutApi from '../api/rollOutApi';
import * as RollsOutActions from '../actions/rollOutActions';
import * as JobResultApi from '../api/jobResultApi';
import { getJobsByIdsApi } from '../api/jobApi';
import { getJobResultApi } from '../api/jobResultApi';
import { printPaletteSaga } from './jobResultSaga';

const getRollOut = state => state.rollOut;
const getLayout = state => state.layout;

// L O A D   R O L L S   O U T
function* loadRollsOutSaga(action) {
    const { locale } = yield select(getLayout);
    const { sort, offset, limit, filters } = yield select(getRollOut);
    let machineIDS = [];

    try {
        const params = {
            limit,
            offset,
            joins: JSON.stringify(['form_versions', 'forms']),
        };

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;

            if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            } else if (sort.key === '_id') {
                params.sort_by = JSON.stringify({ _id: sortInt });
            } else if (sort.key === 'shipping_date') {
                params.sort_by = JSON.stringify({ 'forms.shipping_date': sortInt });
            }
        }

        const sendFilters = {};

        if (filters.rollerBarcode !== '') {
            sendFilters.rolls_out_barcode = operatorTyping(filters.rollerBarcode.trim());
        }

        if (filters.state !== undefined) {
            if (filters.state === 'NOT_DONE') {
                sendFilters.state = ['WAITING_FOR_MANUFACTURING', 'PLANNED', 'IN_PROGRESS'];
            } else {
                sendFilters.state = filters.state;
            }
        }

        if (filters.machine_ids !== undefined) {
            sendFilters.machine_ids = filters.machine_ids;
        }

        if (filters.quality !== '') {
            sendFilters.join = {
                'forms.configurations_property.name.value': operatorTyping(filters.quality.trim()),
            };
        }

        if (filters.formId !== '') {
            sendFilters.join = {
                'forms.form_number': operatorTyping(filters.formId.trim()),
            };
        }

        if (filters.formVersionId !== '') {
            sendFilters.join = {
                'form_versions.form_version_number': operatorTyping(filters.formVersionId.trim()),
            };
        }

        if (filters.width !== '') {
            sendFilters.width = parseFloat(filters.width.trim());
        }

        if (filters.height !== '') {
            sendFilters.height = parseFloat(filters.height.trim());
        }

        if (filters.jobId !== '') {
            sendFilters.join = {
                'form_versions.job_numbers': operatorTyping(filters.jobId.trim()),
            };
        }

        if (filters.creationDate.length > 0) {
            const customDate = [
                moment.utc(filters.creationDate[0]).format('YYYY-MM-DD 00:00:00'),
                moment.utc(filters.creationDate[1]).format('YYYY-MM-DD 23:59:59'),
            ];
            sendFilters.created_at = { between: customDate };
        }

        params.filter = JSON.stringify(sendFilters);

        const result = yield RollsOutApi.getRollsOutApi(params);

        if (result.status === 200) {
            const rollsOut = result.data;

            rollsOut.hits.forEach((rollOut) => {
                rollOut.steps.forEach((step) => {
                    machineIDS.push(step.machine_id);
                });
            });

            machineIDS = uniq(machineIDS);

            const macroMachines = yield* getMachinesMacro(machineIDS);
            const machines = macroMachines.data.hits;

            rollsOut.hits = rollsOut.hits.map(rollOut => ({
                ...rollOut,
                steps: rollOut.steps.map(step => (
                    {
                        ...step,
                        machine_name: machines.find(machine => machine._id === step.machine_id).name,
                    }
                )),
            }));

            yield put(RollsOutActions.loadRollsOutRes(result.data));

            if (action.jps) {
                const resultJobResults = yield getJobResultApi(params);
                const { hits } = resultJobResults.data;

                const jps = hits.filter(hit => action.jps.map(jp => jp.job_palette_id).includes(hit._id));

                jps.forEach((jp) => {
                    if (isNil(jp.palette_number)) {
                        setNotification('error', locale, 'global.error', `Cannot add job result ${jp.job_palette_number} to palette`, { noTranslateMsg: true, duration: 0 });
                    }
                });
            }
        } else {
            yield put(RollsOutActions.loadRollsOutErr());
        }
    } catch (e) {
        yield put(RollsOutActions.loadRollsOutErr());
        console.log(e);
    }
}

export function* loadRollsOutWatcher() {
    yield takeLatest(RollsOutTypes.LOAD_ROLLS_OUT, loadRollsOutSaga);
}
// L O A D   R O L L S   O U T


// T O G G L E   R O L L S   O U T   S T A T U S
function* toggleRollsOutStatusSaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const result = yield RollsOutApi.toggleRollOutStatusApi(action.data);

        if (result.status === 202) {
            if (action.data.step_state === 'IN_PROGRESS') {
                setNotification('success', locale, 'global.success', 'rollOut.notification.start.success');
            } else {
                setNotification('success', locale, 'global.success', 'rollOut.notification.stop.success');
            }

            yield put(RollsOutActions.toggleRollsOutStatusRes());
            yield call(loadRollsOutSaga, {});
        } else if (action.data.step_state === 'IN_PROGRESS') {
            setNotification('error', locale, 'global.error', 'rollOut.notification.start.error');
            yield put(RollsOutActions.toggleRollsOutStatusErr());
        } else if (action.data.step_state === 'DONE') {
            setNotification('error', locale, 'global.error', 'rollOut.notification.stop.error');
            yield put(RollsOutActions.toggleRollsOutStatusErr());
        }
    } catch (e) {
        yield put(RollsOutActions.toggleRollsOutStatusErr());
        console.log(e);
    }
}

export function* toggleRollsOutStatusWatcher() {
    yield takeLatest(RollsOutTypes.TOGGLE_ROLLS_OUT_STATUS, toggleRollsOutStatusSaga);
}
// T O G G L E   R O L L S   O U T   S T A T U S


// C R E A T E   P A L E T T E   F R O M   R O L L S   O U T
function* createPaletteFromRollsOutSaga({ data }) {
    const { locale } = yield select(getLayout);

    try {
        const resultRollsoutInJobResult = yield JobResultApi.getJobResultApi({
            filter: JSON.stringify({ rolls_out_id: data._id }),
        });

        if (resultRollsoutInJobResult.status === 200) {
            if (resultRollsoutInJobResult.data.hits.length === 0) {
                const jobIDS = uniq(flattenDeep(data.form_versions.map(fv => fv.jobs)));

                const resultJobs = yield getJobsByIdsApi(jobIDS);

                if (resultJobs.status === 200) {
                    const bulk = flattenDeep(
                        data.sections.map((section) => {
                            const formVersion = data.form_versions.find(fv => fv._id === section.form_version_id);

                            return formVersion.jobs.map((jobID) => {
                                const job = resultJobs.data.hits.find(hit => hit._id === jobID);

                                return {
                                    job_palette_id: uuidv4(),
                                    job_id: jobID,
                                    roll_out_id: data._id,
                                    quantity: section.repetition * job.quantity / formVersion.repetition,
                                };
                            });
                        }),
                    );

                    const jobResultData = { bulk };

                    const resultJobResults = yield JobResultApi.addJobPaletteApi(jobResultData);
                    if (resultJobResults.status === 202) {
                        const paletteID = uuidv4();

                        const items = bulk.reverse().map((item, index) => ({
                            job_palette_id: item.job_palette_id,
                            palette_number: paletteID,
                            order: index + 1,
                        }));

                        const paletteParams = { items, rolls_out_id: data._id };
                        const resultPalette = yield JobResultApi.addNewPaletteApi(paletteParams);

                        if (resultPalette.status === 202) {
                            yield put(RollsOutActions.createPaletteFromRollsOutRes());
                            yield call(printPaletteSaga, { data: items[0].job_palette_id });
                            setNotification('success', locale, 'global.success', 'rollOut.notification.palette.success');
                        } else {
                            const error = yield resultPalette.json();
                            setNotification('error', locale, 'global.error', typeof error === 'string' ? error : error.message, { noTranslateMsg: true });
                            yield put(RollsOutActions.createPaletteFromRollsOutErr());
                        }

                        yield call(loadRollsOutSaga, { jps: items });
                    } else {
                        const error = yield resultJobResults.json();
                        setNotification('error', locale, 'global.error', typeof error === 'string' ? error : error.message, { noTranslateMsg: true });
                        yield put(RollsOutActions.createPaletteFromRollsOutErr());
                    }
                }
            } else {
                const [firstHit] = resultRollsoutInJobResult.data.hits;
                setNotification('warn', locale, 'global.warning', 'rollOut.notification.palette.exist', { palette: firstHit.palette_number });
                yield put(RollsOutActions.createPaletteFromRollsOutRes());
            }
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'rollOut.notification.palette.error');
        yield put(RollsOutActions.createPaletteFromRollsOutErr());
        console.log(e);
    }
}

export function* createPaletteFromRollsOutWatcher() {
    yield takeLatest(RollsOutTypes.CREATE_PALETTE_FROM_ROLLS_OUT, createPaletteFromRollsOutSaga);
}
// C R E A T E   P A L E T T E   F R O M   R O L L S   O U T
